import type { Path } from "@ostack.tech/kform";
import { type ReactNode, useContext, useMemo } from "react";

import { FormContext, type FormContextValue } from "../contexts/FormContext.ts";
import type { FormatterControllerState } from "../hooks/useFormatter.ts";
import {
  type InputElementProps,
  type InputOptions,
  useInput,
} from "../hooks/useInput.ts";
import { shallow } from "../utils/shallow.ts";

/** Properties of the {@link Input} component. */
export type InputProps<
  T = unknown,
  TFormatted = T | undefined,
  TInput = unknown,
> = InputOptions<T, TFormatted, TInput> & InputOwnProps<T, TFormatted, TInput>;

/** Own properties of the {@link Input} component. */
export interface InputOwnProps<
  T = unknown,
  TFormatted = T | undefined,
  TInput = unknown,
> {
  path?: Path | string;
  children: (
    elementProps: InputElementProps<TFormatted, TInput>,
    state: FormatterControllerState<T, TFormatted>,
  ) => ReactNode;
}

/**
 * Component used to wrap an input so that it "connects" to a form value.
 *
 * This component is a wrapper around the {@link useInput} hook.
 */
export function Input<
  T = unknown,
  TFormatted = T | undefined,
  TInput = unknown,
>({ path, children, ...props }: InputProps<T, TFormatted, TInput>) {
  const formContext = useContext(FormContext);
  const inputController = useInput(path, props);

  const formManager = inputController.useFormManager();
  const currentPath = inputController.usePath();
  const newFormContext = useMemo<FormContextValue>(
    () => ({
      formManager,
      currentPath,
      controller:
        formContext?.formManager === formManager
          ? formContext.controller
          : undefined,
    }),
    [
      currentPath,
      formContext?.controller,
      formContext?.formManager,
      formManager,
    ],
  );

  const state = inputController.useState((state) => state, {
    equalityFn: shallow,
  });
  return (
    <FormContext.Provider value={newFormContext}>
      {children(inputController.inputProps, state)}
    </FormContext.Provider>
  );
}
